import { useScrollLock } from "@/hooks/useScrollLock";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Grid, Modal } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TextFormatter from "../../utils/textFormatter";
import CitelistModal from "./CitelistModal";

interface QABrowserModalProps {
  open: boolean;
  onClose: () => void;
  question?: string;
  answer?: string;
  modelName?: string;
  linksNum?: number;
  taskId?: string;
  brandId?: string;
  categoryId?: string;
  days?: number;
  metricType?: string;
  modelId?: string;
  onPrevQuestion?: () => void;
  onNextQuestion?: () => void;
  hasPrevQuestion?: boolean;
  hasNextQuestion?: boolean;
}

export const QABrowserModal: React.FC<QABrowserModalProps> = ({
  open,
  onClose,
  question,
  answer,
  modelName,
  linksNum,
  taskId,
  brandId,
  categoryId,
  days,
  metricType,
  modelId,
  onPrevQuestion,
  onNextQuestion,
  hasPrevQuestion = false,
  hasNextQuestion = false,
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [citelistModalVisible, setCitelistModalVisible] = useState(false);

  // 防止移动端滚动穿透
  useScrollLock(open);

  // 粒子动画（简化：仅保留容器，避免影响修复）
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    // 保留节点，后续如需还原动画再启用
  }, []);

  const colors = useMemo(
    () => ({
      primary: "#21C4B8",
      border: "rgba(43, 189, 178, 0.2)",
      text: "#0f172a",
      textSecondary: "#475569",
    }),
    []
  );

  // 与 ReputationDetailModal 统一：蒙层与内容玻璃效果
  useEffect(() => {
    if (!open) return;
    const styleId = "qa-browser-glass-styles";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .qa-browser-modal .ant-modal-mask {
        background: linear-gradient(135deg, rgba(43,189,178,0.10) 0%, rgba(15,108,117,0.15) 50%, rgba(43,189,178,0.10) 100%);
        backdrop-filter: blur(8px);
      }
      .qa-browser-modal .ant-modal-content {
        background: rgba(255,255,255,0.9);
        backdrop-filter: blur(18px);
        border: 1px solid rgba(43,189,178,0.2);
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById(styleId);
      if (el) document.head.removeChild(el);
    };
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterClose={() => {}}
      footer={null}
      maskClosable={true}
      title={null}
      centered
      width={isMobile ? "92vw" : "90%"}
      rootClassName="qa-browser-modal"
      closable={false} // 明确禁用默认关闭按钮
      styles={{
        content: { padding: 0, borderRadius: 6, overflow: "hidden" },
        body: { padding: 0, borderRadius: 6 },
      }}
    >
      {/* 背景画布（预留） */}
      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* 主要内容区域 */}
      <div
        style={{ position: "relative", zIndex: 2, padding: isMobile ? 12 : 40 }}
      >
        {/* 标题 */}
        <div
          style={{
            textAlign: "center",
            marginBottom: isMobile ? 12 : 32,
            position: "relative",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? 28 : 36,
              fontWeight: 800,
              color: "#4BA67A",
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {t("common.modals.ai_qa_browser_title")}
          </div>
          {/* 关闭按钮 */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "none",
              border: "none",
              fontSize: isMobile ? 20 : 24,
              color: "#666",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
              e.currentTarget.style.color = "#333";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = "#666";
            }}
          >
            ×
          </button>
        </div>

        {/* 当前问题 */}
        {question && (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.6)",
              padding: isMobile ? 12 : 20,
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              marginBottom: isMobile ? 20 : 24,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{ fontWeight: 600, color: colors.text, fontSize: 16 }}
                >
                  {t("common.modals.current_query")}
                </div>
                {modelName && (
                  <span style={{ color: colors.textSecondary, fontSize: 14 }}>
                    {modelName}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  size={isMobile ? "small" : "middle"}
                  disabled={!hasPrevQuestion}
                  onClick={onPrevQuestion}
                >
                  <LeftOutlined /> {t("common.modals.reputation_previous")}
                </Button>
                <Button
                  size={isMobile ? "small" : "middle"}
                  disabled={!hasNextQuestion}
                  onClick={onNextQuestion}
                >
                  {t("common.modals.reputation_next")} <RightOutlined />
                </Button>
              </div>
            </div>
            <div
              style={{
                color: colors.textSecondary,
                lineHeight: 1.6,
                fontSize: 14,
                marginBottom: 0,
                textAlign: "left",
              }}
            >
              {question}
            </div>
          </div>
        )}

        {/* 答案区 */}
        <div
          style={{
            background: "rgba(248, 250, 252, 0.8)",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            height: "60vh",
            overflow: "auto",
            position: "relative",
          }}
        >
          {/* 可点击文字行 */}
          <div
            style={{
              padding: isMobile ? "12px 16px" : "16px 20px",
              backgroundColor: "rgba(248, 250, 252, 0.8)",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(43, 189, 178, 0.1)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "rgba(248, 250, 252, 0.8)")
            }
            onClick={() => {
              setCitelistModalVisible(true);
            }}
          >
            <div
              style={{
                color: colors.primary,
                fontSize: 14,
                fontWeight: 500,
                textAlign: "left",
              }}
            >
              {t("common.modals.reputation_search_web")}（{linksNum || 0}）
              {/* {t("common.modals.reputation_search_web_suffix")} */}
            </div>
          </div>

          {/* 答案内容区 */}
          <div style={{ padding: isMobile ? 16 : 20 }}>
            {answer && answer.trim() ? (
              <TextFormatter
                content={
                  answer
                    // 处理 [1,3,5,7](@ref) 格式，拆分成单独的 [数字]
                    .replace(
                      /\[([\d,]+)\]\(@ref\)/g,
                      (match: string, numbers: string) => {
                        const numberArray = numbers.split(",");
                        return numberArray
                          .map((num: string) => `[${num.trim()}]`)
                          .join(" ");
                      }
                    )
                    .replace(/\[citation:(\d+)\]/g, " [$1]")
                    .replace(/<br\s*\/?>/gi, "；")
                    .replace(/\[\^(\d+)\^\]/g, " [$1]") ||
                  t("common.modals.no_content")
                }
                colors={colors as any}
                isMobile={isMobile}
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  color: colors.textSecondary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.8,
                }}
              >
                {t("common.modals.no_answers")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CitelistModal */}
      <CitelistModal
        open={citelistModalVisible}
        onClose={() => setCitelistModalVisible(false)}
        title={t("search_detail.insight_related_articles")}
        taskId={taskId}
        brandId={brandId}
        categoryId={categoryId}
        days={days}
        metricType={metricType}
        modelId={modelId}
        onItemClick={(item) => {
          // 处理文章点击事件，在新窗口打开
          window.open(item.url, "_blank");
        }}
      />
    </Modal>
  );
};

export default QABrowserModal;
